import { ApiDataContext } from "contexts/ApiDataContext";
import { useAutocomplete } from "hooks/forms/FormHooks";
import { useContext, useState } from "react";
import { useManagementTextField } from "../../../hooks/forms/ManagementHooks";
import { useRadioGroup } from "../../../hooks/forms/RadioGroupHooks";

export default function useDedicatedAttorneyForm() {
  const { apiData } = useContext(ApiDataContext);
  const [clientValue, setClientValue] = useState("");
  const [clientsPDCs, setClientsPDCs] = useState([]);

  const handleClientChange = async (event, newValue) => {
    setClientValue(newValue);
    setClientsPDCs(newValue.clientsPDC);
  };

  const clientType = useRadioGroup("PERSON");
  const { reset: resetCPF, ...cpf } = useManagementTextField("");
  const { reset: resetCNPJ, ...cnpj } = useManagementTextField("");
  const { reset: resetRepName, ...repName } = useManagementTextField("");
  const { reset: resetRepNationality, ...repNationality } = useManagementTextField("");
  const { reset: resetRg, ...rg } = useManagementTextField("");
  const { reset: resetIssuingBody, ...issuingBody } = useManagementTextField("");

  const { reset: resetProjectName, ...projectName } = useManagementTextField("");
  const { reset: resetPDC, ...powerDistributionCompany } = useAutocomplete("", false);
  const { reset: resetEngineer, ...engineer } = useAutocomplete("", false);

  const client = {
    value: clientValue,
    onChange: handleClientChange,
  };

  const validateFields = () => {
    let isValid = true;

    // Validar campos comuns
    if (!projectName.isFieldValid()) isValid = false;
    if (!powerDistributionCompany.isFieldValid()) isValid = false;
    if (!engineer.isFieldValid()) isValid = false;

    // Validar campos específicos por tipo
    if (clientType.value === "PERSON") {
      const cpfDigits = cpf.value.replace(/\D/g, '');
      if (cpfDigits.length === 0) {
        cpf.setCustomError("O campo é obrigatório");
        isValid = false;
      } else if (cpfDigits.length < 11) {
        cpf.setCustomError("CPF incompleto");
        isValid = false;
      } else {
        cpf.removeFieldError();
      }
    } else {
      const cnpjDigits = cnpj.value.replace(/\D/g, '');
      if (cnpjDigits.length === 0) {
        cnpj.setCustomError("O campo é obrigatório");
        isValid = false;
      } else if (cnpjDigits.length < 14) {
        cnpj.setCustomError("CNPJ incompleto");
        isValid = false;
      } else {
        cnpj.removeFieldError();
      }
      if (!repName.isFieldValid()) isValid = false;
      if (!repNationality.isFieldValid()) isValid = false;
    }

    return isValid;
  };

  const clearAllFields = () => {
    clientType.onCustomChange("");
    cpf.onCustomChange("");
    cnpj.onCustomChange("");
    repName.onCustomChange("");
    repNationality.onCustomChange("");
    rg.onCustomChange("");
    issuingBody.onCustomChange("");
    projectName.onCustomChange("");
    resetPDC();
    resetEngineer();
  };

  const values = () => {
    return {
      clientType: clientType.value,
      client: clientValue,
      cpf: cpf.value,
      cnpj: cnpj.value,
      repName: repName.value,
      repNationality: repNationality.value,
      rg: rg.value,
      issuingBody: issuingBody.value,
      projectName: projectName.value,
      powerDistributionCompany: powerDistributionCompany.value,
      engineer: engineer.value,
    };
  };

  const returnBody = (address) => {
    const project = values();

    const state = apiData.states.find(
      (state) => state.id === project.powerDistributionCompany?.powerDistributionCompanies?.state
    );

    const stateName = state?.name || "";

    const projectType = project.clientType;

    return {
      projectName: project.projectName,
      isInternalProject: Boolean(project.client.internalClient),

      // Dados pessoais - Pessoa Física
      cpf: projectType === "PERSON" ? project.cpf : undefined,
      rg: projectType === "PERSON" ? project.rg : undefined,
      issuingBody: projectType === "PERSON" ? project.issuingBody : undefined,

      // Dados pessoais - Pessoa Jurídica (Representante Legal)
      repCpf: projectType === "ENTITY" ? project.cpf : undefined,
      repRg: projectType === "ENTITY" ? project.rg : undefined,
      repIssuingBody: projectType === "ENTITY" ? project.issuingBody : undefined,

      cnpj: project.cnpj || undefined,
      repName: project.repName || undefined,
      repNationality: project.repNationality || undefined,

      // Endereço do outorgante (preenchido no formulário)
      street: address.street,
      number: address.number,
      district: address.district,
      city: address.city,
      state: address.state,
      zipCode: address.zipCode,
      addressDetails: address.addressDetails,

      // Dados do cliente/integrador (para segunda seção)
      client: {
        name: project.client.name,
        type: project.client.cnpj ? "ENTITY" : "PERSON",
        cnpj: project.client.cnpj || null,
        street: project.client.street,
        number: project.client.number,
        district: project.client.district,
        city: project.client.city,
        state: project.client.state,
        zipCode: project.client.zipCode,
        addressDetails: project.client.addressDetails,
      },

      // Dados do engenheiro
      engineer: {
        name: project.engineer.name,
        nationality: project.engineer.nationality || "",
        phone: project.engineer.phone || "",
        cpf: project.engineer.cpf || "",
        rg: project.engineer.rg || "",
        issuingBody: project.engineer.issuingBody || "",
        street: project.engineer.street || "",
        number: project.engineer.number || "",
        district: project.engineer.district || "",
        city: project.engineer.city || "",
        state: project.engineer.state || "",
        zipCode: project.engineer.zipCode || "",
        addressDetails: project.engineer.addressDetails || "",
      },

      powerDistributionCompanies: {
        name: project.powerDistributionCompany.powerDistributionCompanies.name,
        stateName,
      },
    };
  };

  return {
    cpf,
    cnpj,
    clientType,
    repName,
    repNationality,
    rg,
    issuingBody,
    projectName,
    powerDistributionCompany,
    engineer,
    client,
    clientValue,
    apiClients: apiData.clients || [],
    apiEngineers: apiData.engineers || [],
    clientsPDCs,
    validateFields,
    values,
    clearAllFields,
    returnBody,
  };
}
